
import React, { useState, useCallback, useEffect } from "react";
import { Building2, Search, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useCompanySearch } from "./useCompanySearch";
import { CompanyDetails, CompanySearchResult } from "@/lib/api/companyRegister";

interface CompanySearchInputProps {
  onSelect: (company: CompanyDetails) => void;
  defaultValue?: string;
  className?: string;
  onChange?: (value: string) => void;
}

export function CompanySearchInput({ 
  onSelect, 
  defaultValue = "", 
  className = "",
  onChange 
}: CompanySearchInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [inputValue, setInputValue] = useState(defaultValue);
  
  const {
    isAvailable,
    isSearching,
    searchResults,
    search,
    getDetails,
    clearResults
  } = useCompanySearch();
  
  // Handle search
  const handleSearch = useCallback(async (query: string) => {
    if (query.length < 2 || !isAvailable) return;
    console.log("Handling search for query:", query);
    await search(query);
  }, [isAvailable, search]);
  
  // Handle selecting a company
  const handleSelectCompany = useCallback(async (result: CompanySearchResult) => {
    try {
      console.log("Selected company:", result);
      const details = await getDetails(result);
      
      if (details) {
        onSelect(details);
        setIsOpen(false);
        setSearchTerm("");
        setInputValue(details.name);
        clearResults();
      }
    } catch (error) {
      console.error("Error getting company details:", error);
    }
  }, [getDetails, onSelect, clearResults]);

  // Handle manual input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    if (onChange) {
      onChange(value);
    }
  };
  
  // Update input value when defaultValue changes
  useEffect(() => {
    setInputValue(defaultValue);
  }, [defaultValue]);
  
  return (
    <div className={`relative flex ${className}`}>
      <Input
        type="text"
        placeholder="Enter supplier name"
        value={inputValue}
        className="rounded-r-none"
        onChange={handleInputChange}
      />
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="rounded-l-none border-l-0 px-2"
            aria-label="Search company register"
          >
            <Building2 className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="end">
          <div className="p-4 space-y-4">
            <h3 className="font-medium">Search Company Register</h3>
            <div className="relative">
              <Input
                type="text"
                placeholder="Enter company name or org number"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch(searchTerm);
                  }
                }}
              />
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="absolute right-0 top-0 h-10 px-3"
                onClick={() => handleSearch(searchTerm)}
              >
                {isSearching ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
              </Button>
            </div>
            
            {/* Search results */}
            <div className="max-h-[300px] overflow-y-auto border rounded-md">
              {searchResults.length > 0 ? (
                <div>
                  {searchResults.map((company) => (
                    <button
                      key={company.id}
                      className="w-full p-3 text-left hover:bg-muted border-b last:border-b-0 focus:outline-none focus:bg-muted"
                      onClick={() => handleSelectCompany(company)}
                    >
                      <div className="font-medium">{company.name}</div>
                      {company.organizationNumber && (
                        <div className="text-sm text-muted-foreground">
                          Org.nr: {company.organizationNumber}
                        </div>
                      )}
                      {company.address && (
                        <div className="text-sm text-muted-foreground">
                          {company.address}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              ) : isSearching ? (
                <div className="p-4 text-center text-muted-foreground">
                  Searching...
                </div>
              ) : searchTerm.length > 0 ? (
                <div className="p-4 text-center text-muted-foreground">
                  No results found. Try another search term.
                </div>
              ) : (
                <div className="p-4 text-center text-muted-foreground">
                  Enter a company name or organization number
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
